import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { track } from '@/lib/analytics';
import { Check, ArrowRight, BarChart3, TrendingUp, Map, Sparkles } from 'lucide-react';

/**
 * Landing "Investitori" — targeting HNWI, aziende agricole in espansione,
 * fondi immobiliari specializzati. Toni professionale, focus su dati e analisi AI.
 */
export default function Investitori() {
  useEffect(() => {
    track('landing_view', { audience: 'investitori' });
    document.title = 'Investi in terreni agricoli con analisi AI — AgriNexus';
    const set = (sel, attr, val) => document.querySelector(sel)?.setAttribute(attr, val);
    set('meta[name="description"]', 'content', 'Scopri terreni agricoli in vendita in tutta Italia con analisi AI: clima, altitudine, potenziale colturale, prezzi comparabili. Investimento consapevole.');
  }, []);

  const onCTA = (name) => track('landing_cta', { audience: 'investitori', cta: name });

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground to-terracotta/30 opacity-95"/>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24 md:py-32 text-background">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-accent mb-5">Per investitori e imprenditori</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            La terra come <span className="italic text-accent">asset class</span>.<br/>Dati, non intuizioni.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-background/75 max-w-2xl">
            Ogni terreno su AgriNexus include un report AI con clima ECMWF, altitudine satellitare, colture compatibili e prezzi comparabili di zona. Decidi con evidenze.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/esplora?listing_type=sale" onClick={() => onCTA('explore_hero')} data-testid="landing-cta-explore" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-accent text-foreground font-medium hover:opacity-90 transition">
              Esplora terreni in vendita <ArrowRight size={16}/>
            </Link>
            <Link to="/preferenze" onClick={() => onCTA('prefs_hero')} className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-background/30 text-background/90 hover:bg-background/10 transition">
              Imposta i tuoi criteri
            </Link>
          </div>
        </div>
      </section>

      {/* KPI ROW */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            ['Dati climatici', 'ECMWF ERA5 · 5 anni', BarChart3],
            ['Compatibilità colturale', 'Score 0-100 con AI', Sparkles],
            ['Geolocalizzazione', 'Precisione OSM/satellite', Map],
            ['Trend prezzi zona', 'Prossimamente', TrendingUp],
          ].map(([t,d,I])=>(
            <div key={t} className="rounded-2xl border border-border p-6 bg-card">
              <I size={22} className="text-terracotta" strokeWidth={1.6}/>
              <div className="mt-4 font-display text-lg tracking-tight">{t}</div>
              <div className="text-xs text-muted-foreground mt-1">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* PORTFOLIO APPROACH */}
      <section className="bg-secondary/40 py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-terracotta">Approccio portafoglio</div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter mt-4">Diversifica <span className="italic">per regione, coltura, orizzonte temporale.</span></h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              AgriNexus non è solo un marketplace: è uno strumento di analisi. Salva ricerche mirate (es. "Vigneti Toscana &lt; 300k€"), ricevi alert email per ogni nuovo terreno che matcha i tuoi criteri, confronta report AI multipli.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                'Filtra per superficie, prezzo, regione, coltura, caratteristiche tecniche',
                'Confronta report AI con score di compatibilità 0-100',
                'Ricerche salvate con notifiche email automatiche',
                'Contatto diretto con proprietari senza intermediari',
              ].map(t=>(
                <li key={t} className="flex gap-3 text-sm">
                  <Check size={18} className="text-accent shrink-0 mt-0.5" strokeWidth={2.4}/>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <img src="https://images.unsplash.com/photo-1592982537447-6f2a6a0c8b6e?auto=format&fit=crop&w=1200&q=80" alt="" className="rounded-2xl w-full h-[520px] object-cover shadow-xl" onError={(e)=>{e.currentTarget.src='https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=1200&q=80'}}/>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-24 text-center">
        <h2 className="font-display text-4xl md:text-6xl tracking-tighter">
          La prossima<br/><span className="italic text-terracotta">generazione</span> di land investing.
        </h2>
        <div className="mt-10 flex flex-wrap gap-3 justify-center">
          <Link to="/esplora?listing_type=sale" onClick={() => onCTA('explore_footer')} className="inline-flex items-center gap-2 h-14 px-9 rounded-full bg-foreground text-background text-base font-medium hover:opacity-90 transition">
            Vedi i terreni in vendita <ArrowRight size={18}/>
          </Link>
          <Link to="/registrati" onClick={() => onCTA('signup_footer')} className="inline-flex items-center gap-2 h-14 px-9 rounded-full border border-border text-base font-medium hover:bg-secondary transition">
            Crea account gratuito
          </Link>
        </div>
      </section>
    </div>
  );
}
